/**
 * Everything the advisor already chose, put back before the app paints.
 *
 * Four preferences, one pattern, and the pattern is the point: each has a
 * synchronous `applyCached*` that reads a local copy, and an async
 * `sync*FromCloud` that reconciles. The cached call happens BEFORE the first
 * paint and the sync runs unawaited, because a correction is not a
 * precondition — blocking the app on four network reads would trade a flash
 * for a stall, four times over.
 *
 * Split out of `main.js` on 27 Aug 2026 when navigation became the fourth and
 * took that file past 400 lines. It is a real seam and not just a place to
 * cut: `main.js` boots the app and routes it, and this answers one separate
 * question — what did this person set last time. A fifth preference belongs
 * here, next to the other four, rather than as another paragraph in boot.
 *
 * ── Order matters, once ──────────────────────────────────────────────────
 *
 * `applyCachedNav` must land before the shell mounts. The shell reads
 * `data-nav` when it renders, so an advisor on the sidebar would otherwise
 * get a top bar for one frame and watch the page jump 72px sideways under a
 * cursor they were already moving. Everything else here is order-independent.
 */

import { applyCachedTheme, syncThemeFromCloud } from './theme-store.js'
import { applyCachedBrand, syncBrandFromCloud } from './brand-store.js'
import { applyCachedAppearance, syncAppearanceFromCloud } from './appearance-store.js'
import { applyCachedNav, syncNavFromCloud } from './nav-store.js'

/**
 * @param {string} uid
 * @returns {void} - synchronous by design; the reconciles run unawaited
 */
export function restorePreferences(uid) {
  /* Theme before paint. `applyCachedTheme` is synchronous — it reads the
     copy `/app` keeps in localStorage — so a Slate user never sees a light
     screen. The cloud reconcile runs unawaited: it is a correction, not a
     precondition, and blocking the whole app on it would trade a flash for
     a stall. */
  applyCachedTheme(uid)
  syncThemeFromCloud(uid).catch((err) => console.error('theme sync failed', err))

  /* The advisor's own colours and fonts, on the same two-phase pattern and
     for the same reason. `lib/brand.js` was written, tested and imported by
     NOTHING until 17 Aug 2026 — every button, focus ring and active tab in
     /v2 used the default lavender no matter what an advisor had chosen, and
     the twelve faces the Account screen offers were unreachable. This is the
     line that was missing. */
  applyCachedBrand(uid)
  syncBrandFromCloud(uid).catch((err) => console.error('brand sync failed', err))

  /* View format and text size, same pattern again. Both have been in
     `/app`'s settings modal since the beginning and in the audit table as
     MISSING since the port began: an advisor who set "All pages" and "+3pt"
     opened the new editor and got one cramped page. */
  applyCachedAppearance(uid)
  syncAppearanceFromCloud(uid).catch((e) => console.error('appearance sync failed', e))

  /* Navigation, same two-phase pattern — and the one where a flash would be
     worst. The shell reads `data-nav` when it mounts, so this has to land
     BEFORE the shell mounts or an advisor on the sidebar gets a top bar
     for one frame and the whole page shifts 72px sideways under the cursor
     they were already moving. `applyCachedNav` is synchronous for exactly
     that reason; the cloud reconcile is a correction and runs unawaited. */
  applyCachedNav(uid)
  syncNavFromCloud(uid).catch((e) => console.error('navigation sync failed', e))
}
